/**
 * Once-per-account guard for the `Signup Completed` analytics event.
 *
 * The event must fire exactly once per account: not on refresh, not on a
 * later sign in. Keyed per user id rather than a single shared key so two
 * accounts used in the same browser each get their own guard.
 */

const SIGNUP_TRACKED_KEY_PREFIX = 'ampersand_signup_completed_';

const signupTrackedKey = (userId: string) => `${SIGNUP_TRACKED_KEY_PREFIX}${userId}`;

// second guard for browsers where localStorage is unavailable (private mode,
// blocked storage) and for React StrictMode's double-invoked effects
const trackedInSession = new Set<string>();

/**
 * Whether the `Signup Completed` event has already been sent for this user
 */
export const hasTrackedSignup = (userId: string): boolean => {
  if (trackedInSession.has(userId)) return true;

  try {
    return localStorage.getItem(signupTrackedKey(userId)) === '1';
  } catch (error) {
    console.error('Failed to read signup tracking flag:', error);
    // fall back to the in-memory guard: one event per page load at worst,
    // rather than none at all
    return false;
  }
};

/**
 * Record that the `Signup Completed` event has been sent for this user
 */
export const markTrackedSignup = (userId: string): void => {
  trackedInSession.add(userId);

  try {
    localStorage.setItem(signupTrackedKey(userId), '1');
  } catch (error) {
    console.error('Failed to save signup tracking flag:', error);
  }
};
